import React, { useEffect } from "react";
import { Field, FieldArray, useFormikContext, ErrorMessage } from "formik";
import { Tooltip } from "@mui/material";
import { ServiceInformationProps } from "../service-management-props";
import { SelectInputComponent } from "../../../../common/components";
import { AntSwitch } from "../../../../common/components/ant-switch/ant-switch";
import { getServices } from "../../../../api/admin/service-management";
import {
  getProviderId,
  resolveSingleSelectionFlag,
} from "./add-service-helper";

const ServiceInformation: React.FC<ServiceInformationProps> = ({
  isReadOnly,
  actionType,
  setLoading,
  setAlertMessage,
  setAlertType,
  setAlertOpen,
  serviceDetails,
  setServiceDetails,
  serviceCategories,
  setServiceCategories,
  categoryOptions,
  setCategoryOptions,
  selectedCategoryServicesList,
  setSelectedCategoryServicesList,
  subServiceOptions,
  setSubServiceOptions,
  merchandiseOptions,
  setMerchandiseOptions,
}) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    isValid,
    setFieldError,
  } = useFormikContext<any>();
  const [serviceNameError, setServiceNameError] = React.useState<string>("");
  const ErrorText = ({ name }: { name: string }) => (
    <ErrorMessage name={name} component="div" className="error-message" />
  );

  // Access the serviceInformation section from the form
  const serviceInfo = values.serviceInformation;

  // Restore lists from Formik values on mount or when category/service changes
  useEffect(() => {
    // Restore services list if category is selected
    if (serviceCategories.length && serviceInfo.serviceCategory) {
      const selectedCategory = serviceCategories.find(
        (cat) => cat.name === serviceInfo.serviceCategory,
      );
      if (selectedCategory) {
        const formattedServices = (selectedCategory.services || []).map(
          (service: any) => ({
            value: service.name,
            label: service.name,
            id: service.id,
          }),
        );
        setSelectedCategoryServicesList(formattedServices);
      }
    }
  }, [
    serviceCategories,
    serviceInfo.serviceCategory,
    setSelectedCategoryServicesList,
  ]);

  const handleSelectCategory = (id: number) => {
    // Filter services based on selected category
    const filteredCategory = serviceCategories.find((cat) => cat.id === id);
    const servicesList = filteredCategory?.services || [];
    const formattedServices = servicesList.map((service: any) => ({
      value: service.name,
      label: service.name,
      id: service.id,
    }));
    setSelectedCategoryServicesList(formattedServices);
  };

  const fetchServiceDetails = async (id: number) => {
    setLoading?.(true);
    try {
      const response = await getServices({}, id);
      if (response.status === 200) {
        const details = response?.data?.data;

        const formattedData = {
          id: details.id,
          name: details.name,
          code: details.code,
          needType:
            (details?.service?.needType || details?.service?.needtype || details?.needType || details?.needtype) === "pre_need"
              ? "Preneed"
              : (details?.service?.needType || details?.service?.needtype || details?.needType || details?.needtype) === "at_need"
                ? "Immediate Need"
                : (details?.service?.needType || details?.service?.needtype || details?.needType || details?.needtype) || "-",
          description: details.description,
          category: details.categories.map((cat: any) => cat.name),
          status:
            details.status === true || details.status === "active"
              ? true
              : false,
          minPrice: details.basePrice?.min || 0,
          maxPrice: details.basePrice?.max || 0,
          taxApplicable: details.taxApplicable,
          commissionPercent: details.commissionPercentage,
          commissionValue: details.commissionFixed,
          singleSubServiceSelection: resolveSingleSelectionFlag(
            details,
            "subService",
          ),
          singleMerchandiseSelection: resolveSingleSelectionFlag(
            details,
            "merchandise",
          ),
          subServices: details.subServices.map((subService: any) => ({
            id: subService.id || null,
            name: subService.name,
            description: subService.description,
            minPrice: subService.minPrice || 0,
            maxPrice: subService.maxPrice || 0,
            status: subService.status,
          })),
          merchandises: details.merchandises.map((merchandise: any) => ({
            id: merchandise.id || null,
            name: merchandise.name,
            code: merchandise.code,
            description: merchandise.description,
            minPrice: merchandise.minPrice,
            maxPrice: merchandise.maxPrice,
            status: merchandise.status,
          })),
        };
        setServiceDetails(formattedData);
      }
    } catch (error: any) {
      setAlertMessage?.(
        error?.response?.data?.message || "Failed to fetch service details.",
      );
      setAlertType?.("error");
      setAlertOpen?.(true);
    } finally {
      setLoading?.(false);
    }
  };

  useEffect(() => {
    const serviceId = selectedCategoryServicesList.find(
      (service) => service.value === values?.serviceInformation?.serviceName,
    )?.id;
    if (serviceId && actionType === "create") {
      fetchServiceDetails(serviceId);
      setFieldValue("serviceInformation.serviceId", serviceId);
    } else if (serviceId && actionType !== "create") {
      setFieldValue("serviceInformation.serviceId", serviceId);
    } else if (actionType === "create") {
      // Only clear serviceDetails in create mode when no service is matched.
      // In view/edit mode, serviceDetails is already set by the parent fetch — don't overwrite it.
      setServiceDetails(null);
      setFieldValue("serviceInformation.serviceId", null);
    }
  }, [values?.serviceInformation?.serviceName, selectedCategoryServicesList, actionType]);

  //set sub services and merchandise options based on service details
  useEffect(() => {
    if (serviceDetails) {
      // Only overwrite serviceCode / needType if serviceDetails actually carries them
      // (i.e. when populated from the admin catalog fetch in create mode).
      if (serviceDetails.code !== undefined) {
        setFieldValue("serviceInformation.serviceCode", serviceDetails.code || "");
      }
      if (serviceDetails.needType !== undefined) {
        setFieldValue("serviceInformation.needType", serviceDetails.needType || "");
      }
      setFieldValue(
        "serviceInformation.singleSubServiceSelection",
        !!serviceDetails.singleSubServiceSelection,
      );
      setFieldValue(
        "serviceInformation.singleMerchandiseSelection",
        !!serviceDetails.singleMerchandiseSelection,
      );
      // Filter by status only when the field exists (items from the pricing API
      // have no status field but are inherently active — the provider already selected them).
      const formattedSubServices = serviceDetails.subServices
        .filter((subService: any) => subService.status === undefined || subService.status === "active")
        .map((subService: any) => ({
          value: subService.name,
          label: subService.name,
          id: subService.id,
        }));
      setSubServiceOptions(formattedSubServices);

      const formattedMerchandises = serviceDetails.merchandises
        .filter((merchandise: any) => merchandise.status === undefined || merchandise.status === "active")
        .map((merchandise: any) => ({
          value: merchandise.name,
          label: merchandise.name,
          id: merchandise.id,
        }));
      setMerchandiseOptions(formattedMerchandises);

      // Only prune selected values when there are actual options to validate against.
      // If formattedSubServices is non-empty, remove any stale selections not in the new list.
      if (formattedSubServices.length > 0) {
        setFieldValue(
          "serviceInformation.subServiceIds",
          (values.serviceInformation.subServiceIds || []).filter(
            (selected: string) =>
              formattedSubServices.some((opt: any) => opt.value === selected),
          ),
        );
      }
      if (formattedMerchandises.length > 0) {
        setFieldValue(
          "serviceInformation.merchandiseIds",
          (values.serviceInformation.merchandiseIds || []).filter(
            (selected: string) =>
              formattedMerchandises.some((opt: any) => opt.value === selected),
          ),
        );
      }
    } else if (actionType === "create") {
      setSubServiceOptions([]);
      setMerchandiseOptions([]);
      setFieldValue("serviceInformation.subServiceIds", []);
      setFieldValue("serviceInformation.merchandiseIds", []);
      setFieldValue("serviceInformation.singleSubServiceSelection", false);
      setFieldValue("serviceInformation.singleMerchandiseSelection", false);
    }
  }, [serviceDetails, actionType]);

  return (
    <div className="service-form-card provider-service-form-grid-three-columns">
      <div className="form-field">
        <label>
          Provider ID<span className="required">*</span>
        </label>
        <Field
          name="serviceInformation.providerId"
          placeholder="Enter provider ID"
          value={getProviderId()}
          disabled={isReadOnly}
          readOnly={true}
          onBlur={() => {
            setFieldValue("serviceInformation.providerId", getProviderId());
          }}
        />
        <ErrorText name="serviceInformation.providerId" />
      </div>

      <div className="form-field">
        <label>
          Category<span className="required">*</span>
        </label>
        <SelectInputComponent
          fieldName="serviceInformation.serviceCategory"
          options={categoryOptions}
          placeholder="Select Category"
          value={serviceInfo.serviceCategory || []}
          onChange={(_value: any) => { }}
          handleOption={(option) => {
            setFieldValue(
              `serviceInformation.serviceCategory`,
              option?.value || "",
            );
            handleSelectCategory(option?.id);
            setServiceNameError("");
          }}
          notEditable={isReadOnly}
          isShowError={true}
          handleBlur={() => {
            //setProviderId on blur
            setFieldValue("serviceInformation.providerId", getProviderId());
          }}
        />
      </div>

      <div className="form-field">
        <label>
          Service Name/Service Title<span className="required">*</span>
        </label>
        <SelectInputComponent
          fieldName="serviceInformation.serviceName"
          options={selectedCategoryServicesList}
          placeholder="Select Service Name/title"
          value={serviceInfo.serviceName || ""}
          onChange={(value: any) => {
            setFieldValue(`serviceInformation.serviceName`, value || "");
          }}
          handleOpen={() => {
            if (!serviceInfo.serviceCategory) {
              setServiceNameError("Please select a service category first");
            } else {
              setServiceNameError("");
            }
          }}
          notEditable={isReadOnly}
          isShowError={true}
        />
        {serviceNameError && (
          <div className="error-message">{serviceNameError}</div>
        )}
      </div>

      <div className="form-field">
        <label>
          Service Code<span className="required">*</span>
        </label>
        <Field
          name={"serviceInformation.serviceCode"}
          placeholder="Enter service code"
          maxLength={100}
          value={serviceInfo?.serviceCode || ""}
          readOnly={true}
          disabled={isReadOnly}
          onBlur={() => {
            setFieldValue(
              "serviceInformation.serviceCode",
              serviceInfo?.serviceCode || "",
            );
          }}
        />
        <ErrorText name="serviceInformation.serviceCode" />
      </div>

      <div className="form-field">
        <label>Duration</label>
        <Field
          name="serviceInformation.duration"
          placeholder="Enter duration"
          value={serviceInfo.duration}
          disabled={isReadOnly}
          maxLength={100}
        />
        <ErrorText name="serviceInformation.duration" />
      </div>

      {actionType !== "create" && (
        <div className="form-field">
          <label>Need Type</label>
          <Field
            name="serviceInformation.needType"
            placeholder="Need type"
            value={serviceInfo.needType || ""}
            disabled={true}
            readOnly={true}
          />
        </div>
      )}

      {/* Full width fields */}
      <div className="form-field-full-width">
        <label>
          Short Description<span className="required">*</span>
        </label>
        <Field
          name="serviceInformation.serviceShortDescription"
          placeholder="Enter short description"
          maxLength={200}
          value={serviceInfo.serviceShortDescription || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isReadOnly}
        />
        <ErrorText name="serviceInformation.serviceShortDescription" />
      </div>

      <div className="form-field-full-width">
        <label>
          Detailed Description<span className="required">*</span>
        </label>
        <Field
          as="textarea"
          name="serviceInformation.serviceDetailedDescription"
          placeholder="Enter detail description"
          rows={6}
          value={serviceInfo.serviceDetailedDescription || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isReadOnly}
          maxLength={500}
        />
        <ErrorText name="serviceInformation.serviceDetailedDescription" />
      </div>

      <div className="form-field">
        <label>Sub Services <span className="required">*</span></label>
        <div className="field-input-with-toggle">
          <div className="field-input-with-toggle__input">
            <SelectInputComponent
              fieldName="serviceInformation.subServiceIds"
              options={subServiceOptions}
              placeholder="Select sub-services (required)"
              multiple={true}
              value={serviceInfo.subServiceIds || []}
              onChange={(value: any) => {
                setFieldValue(`serviceInformation.subServiceIds`, value || []);
              }}
              notEditable={isReadOnly}
              isShowError={true}
            />
          </div>
          <Tooltip
            title={
              serviceInfo.singleSubServiceSelection
                ? "Allow single select"
                : "Allow multi-select"
            }
            arrow
          >
            <div className="inline-toggle-control">
              <AntSwitch
                checked={!!serviceInfo.singleSubServiceSelection}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setFieldValue(
                    "serviceInformation.singleSubServiceSelection",
                    checked,
                  );
                  setServiceDetails((prev: any) =>
                    prev
                      ? {
                          ...prev,
                          singleSubServiceSelection: checked,
                        }
                      : prev,
                  );
                }}
                disabled={isReadOnly}
              />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="form-field">
        <label>Merchandise</label>
        <div className="field-input-with-toggle">
          <div className="field-input-with-toggle__input">
            <SelectInputComponent
              fieldName="serviceInformation.merchandiseIds"
              options={merchandiseOptions}
              placeholder="Select merchandise"
              multiple={true}
              value={serviceInfo.merchandiseIds || []}
              onChange={(value: any) => {
                setFieldValue(`serviceInformation.merchandiseIds`, value || []);
              }}
              notEditable={isReadOnly}
              isShowError={true}
            />
          </div>
          <Tooltip
            title={
              serviceInfo.singleMerchandiseSelection
                ? "Allow single select"
                : "Allow multi-select"
            }
            arrow
          >
            <div className="inline-toggle-control">
              <AntSwitch
                checked={!!serviceInfo.singleMerchandiseSelection}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setFieldValue(
                    "serviceInformation.singleMerchandiseSelection",
                    checked,
                  );
                  setServiceDetails((prev: any) =>
                    prev
                      ? {
                          ...prev,
                          singleMerchandiseSelection: checked,
                        }
                      : prev,
                  );
                }}
                disabled={isReadOnly}
              />
            </div>
          </Tooltip>
        </div>
      </div>
      {/* <div className="form-field">
        <label>Availability</label>
        <div className="switch-container">
          <AntSwitch
            checked={serviceInfo.availability || false}
            onChange={(event) => {
              setFieldValue(
                "serviceInformation.availability",
                event.target.checked,
              );
            }}
            disabled={isReadOnly}
          />
        </div>
      </div> */}
      {/* Show array-level error for serviceInformation */}
      {typeof errors?.serviceInformation === "string" && (
        <div style={{ color: "red" }}>
          {errors.serviceInformation}
        </div>
      )}
    </div>
  );
};

export default ServiceInformation;
